import { useEffect, useMemo } from "react";
import { Download, ExternalLink } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import TopbarInner from "../components/topbar/TopbarInner";
import { readUnlockedRewards } from "../services/rewards/streakRewards.js";
 
export default function Guides({ user, isPremium = false, longestStreak = 0, openPremium, onThemeToggle }) {
  const guideUnlocked = readUnlockedRewards(user).guide;
  const navigate = useNavigate();
  const { hash } = useLocation();

  /* Le délai laisse ScrollToTop se poser avant de viser le guide. */
  useEffect(() => {
    if (!hash) return undefined;
    const id = window.setTimeout(() => { document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: "smooth", block: "center" }); }, 220);
    return () => window.clearTimeout(id);
  }, [hash]);
 
  // === Liste des guides ===
  const guides = useMemo(
    () => [
      {
        id: "g1",
        title: "Renouer avec sa respiration",
        tier: "free",
        size: "11.8 Mo",
        file: "/guides/guide-respiration.pdf",
        cover: "/guides/respi.jpg",
      },
{
  id: "g2",
  title: "Mieux dormir",
  tier: "refuge",
  size: "29.1 Mo",
  file: "/guides/guide-sommeil.pdf",
  cover: "/guides/sommeil.jpg",
  paperbackUrl:
    "https://www.amazon.fr/21-jours-pour-mieux-dormir/dp/B0FDWLYH42/ref=sr_1_3?dib=eyJ2IjoiMSJ9.YyU72FvXYK3eXVAhN7T79ip0W8kA5qQCuNZYlbNEy2M.vMWhySdns3jqRP3DLEtIvKDcWbbKmASijajzD0S1HAs&dib_tag=se&qid=1778058790&refinements=p_27%3AJ%C3%A9r%C3%A9my+Tondini&s=books&sr=1-3&text=J%C3%A9r%C3%A9my+Tondini",
},
{
  id: "g3",
  title: "Gestion du stress",
  tier: "refuge",
  size: "24.3 Mo",
  file: "/guides/guide-stress.pdf",
  cover: "/guides/stress.jpg",
  paperbackUrl:
    "https://www.amazon.fr/Jours-pour-g%C3%A9rer-son-stress/dp/B0FCCDF9X7/ref=sr_1_1?__mk_fr_FR=%C3%85M%C3%85%C5%BD%C3%95%C3%91&crid=1KFFN0KCZEIGN&dib=eyJ2IjoiMSJ9.8V7Cyc8tAO5MstuabWIDCHvGOHdhwibVQKvpRsyAR-3qShZx246qZmY9Y2UpQCCyAwyCoWGCgFUdZInJAfJTqr2xaJanEjq5rubGbgzaMm8.MyflcuAsahunZ0Su8lDOgHA3dvcoaxRfEO0HBfdKFpA&dib_tag=se&keywords=21+jour+pour+g%C3%A9rer+son+stress&qid=1778057730&s=books&sprefix=21+jour+pour+g%C3%A9rer+son+stes%2Cstripbooks%2C203&sr=1-1",
},
{
  id: "g4",
  title: "Créer son refuge au quotidien",
  tier: "streak",
  size: "8.4 Mo",
  file: "/guides/guide-refuge.pdf",
  cover: "/guides/refuge.jpg",
},
    ],
    []
  );
 
  return (
    <>
<TopbarInner
  title="Guides"
  onBack={() => navigate("/")}
  onThemeToggle={onThemeToggle}
  isPremium={isPremium}
  longestStreak={longestStreak}
  titleColor="#36495B"
/>
 
      <div className="guides-page" role="main">
 
        <header className="page-hero">
          <p className="page-hero__eyebrow">
            Pour aller encore plus loin 
          </p>

          <h1 className="page-hero__title">Guides</h1>

          <p className="page-hero__lead">
            Des guides pratiques à lire à votre rythme, pour prolonger
            vos moments de calme.
          </p>
        </header>
 
        {/* ── Grille des guides ── */}
        <div className="guides-grid">
          {guides.map((g) => {
            const lockedRefuge = g.tier === "refuge" && !isPremium;
            const lockedStreak = g.tier === "streak" && !guideUnlocked;
            const locked = lockedRefuge || lockedStreak;
            return (
<article
  key={g.id}
  id={`guide-${g.id}`}
  className={`guide-card ${locked ? "guide-card--locked" : ""}`}
>
                <img
                  src={g.cover}
                  alt={`Illustration du guide ${g.title}`}
                  className="guide-cover"
                />
 
                <div className="guide-header">
                  <div className="guide-title">
                    <span className="download-chip" aria-hidden>
                      <Download size={18} />
                    </span>
                    <h2 title={g.title}>{g.title}</h2>
                  </div>
 
                  <div className="guide-info">
                    <span className="size">{g.size}</span>
                  </div>
                </div>
 
                <div className="soft-divider" />
 
{lockedStreak ? (
  <div
    className="locked-guide locked-guide--streak"
    role="region"
    aria-label="Guide offert après sept jours de pratique"
  >
    <div className="lock-left">
      <span className="guide-streak-mark" aria-hidden="true">🌿</span>

      <div>
        <p className="locked-text">Offert après 7 jours de pratique</p>
        <p className="locked-description">Il vous attendra ici, simplement en revenant chaque jour.</p>
      </div>
    </div>
  </div>
) : lockedRefuge ? (
  <div
    className="locked-guide"
    role="region"
    aria-label="Guide inclus dans Le Refuge Personnel"
  >
    <div className="lock-left">
      <span
        className="guide-refuge-mark"
        aria-hidden="true"
      >
        <img
          className="guide-refuge-mark__logo"
          src="/refuge-symbol.svg"
          alt=""
        />
      </span>

      <div>
        <p className="locked-text">Inclus dans Le Refuge Personnel</p>
        <p className="locked-description">Accédez à ce guide avec votre abonnement.</p>
      </div>
    </div>

    <div className="locked-cta">
      <button
        type="button"
        className="guide-refuge-cta"
        onClick={() => openPremium?.()}
      >
        Découvrir le Refuge
      </button>
    </div>
  </div>
) : (
<div className="guide-body">
<a
  href={g.file}
  target="_blank"
  rel="noopener noreferrer"
  className="download-btn"
  aria-label={`Ouvrir le guide ${g.title}`}
>
  <Download size={17} aria-hidden />
  <span>Ouvrir le guide</span>
</a>
                  </div>
                )}
{g.paperbackUrl && (
<div className="paperback-box">
  <a
    href={g.paperbackUrl}
    target="_blank"
    rel="noopener noreferrer"
    className="paperback-link"
    aria-label={`Découvrir ${g.title} en version brochée sur Amazon`}
  >
    <span>Existe aussi en version brochée</span>
    <ExternalLink size={15} aria-hidden />
  </a>
</div>
)}
              </article>
            );
          })}
        </div>
 
        {/* ── Styles ── */}
        <style>{`
.guides-page,
.guides-page * {
  box-sizing: border-box;
}

.guides-page {
  width: 100%;
  min-height: 100%;
  margin-top: 88px;
  padding: 0 20px 80px;

  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 42px;
}

/* Grille */

.guides-grid {
  width: 100%;
  max-width: 1040px;

  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 24px;
}

/* Carte */

.guide-card {
  position: relative;
  min-width: 0;
  padding: 18px;

  display: flex;
  flex-direction: column;

  background: rgba(255, 255, 255, 0.94);
  border: 1px solid rgba(97, 131, 122, 0.16);
  border-radius: 24px;

  box-shadow:
    0 16px 38px rgba(54, 73, 91, 0.07),
    inset 0 1px 0 rgba(255, 255, 255, 0.8);

  transition:
    transform 260ms ease,
    box-shadow 260ms ease,
    border-color 260ms ease;
}

.guide-card--locked {
  background: rgba(255, 255, 255, 0.94);
  border-color: rgba(97, 131, 122, 0.34);
  box-shadow: 0 16px 38px rgba(54, 73, 91, 0.07), 0 0 0 1px rgba(97, 131, 122, 0.05), inset 0 1px 0 rgba(255, 255, 255, 0.8);
}

/* Visuel */

.guide-cover {
  width: 100%;
  height: 220px;
  margin-bottom: 20px;

  display: block;
  object-fit: cover;

  border-radius: 18px;
  background: #f1f5f3;
}

/* En-tête */

.guide-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;

  margin-bottom: 12px;
}

.guide-title {
  min-width: 0;

  display: flex;
  align-items: center;
  gap: 11px;
}

.download-chip {
  width: 34px;
  height: 34px;
  flex: 0 0 34px;

  display: grid;
  place-items: center;

  color: #56766e;
  background: #edf5f2;
  border: 1px solid #dcebe6;
  border-radius: 50%;
}

.guide-title h2 {
  margin: 0;

  color: #36495b;
  font-size: 1.08rem;
  font-weight: 700;
  line-height: 1.35;

  overflow: hidden;
  text-overflow: ellipsis;
}

.guide-info {
  flex: 0 0 auto;
}

.size {
  color: #7b898f;
  font-size: 0.8rem;
  font-weight: 500;
  white-space: nowrap;
}

.soft-divider {
  width: 100%;
  height: 1px;
  margin: 4px 0 14px;

  background: rgba(97, 131, 122, 0.13);
}

/* Guide disponible */

.guide-body {
  min-height: 82px;
  padding: 12px 4px 4px;

  display: flex;
  align-items: center;
  justify-content: center;
}

.download-btn {
  min-height: 46px;
  padding: 12px 20px;

  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 9px;

  color: #ffffff;
  background: #61837a;
  border: 1px solid #61837a;
  border-radius: 14px;

  font-size: 0.93rem;
  font-weight: 650;
  line-height: 1;
  text-decoration: none;

  box-shadow: 0 8px 20px rgba(97, 131, 122, 0.16);

  transition:
    background 220ms ease,
    border-color 220ms ease,
    box-shadow 220ms ease,
    transform 220ms ease;
}

.download-btn:focus-visible,
.paperback-link:focus-visible {
  outline: 3px solid rgba(118, 165, 167, 0.26);
  outline-offset: 3px;
}

/* Guide verrouillé */

.locked-guide {
  min-height: 82px;
  padding: 15px 16px;

  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 16px;

  color: #36495b;
  background: rgba(237, 245, 242, 0.48);
  border: 1px solid rgba(97, 131, 122, 0.17);
  border-radius: 16px;
}

.lock-left {
  min-width: 0;

  display: flex;
  align-items: flex-start;
  gap: 11px;
}

.guide-refuge-mark {
  width: 30px;
  height: 30px;
  flex: 0 0 30px;
  display: grid;
  place-items: center;
  border: 1px solid rgba(97, 131, 122, 0.28);
  border-radius: 999px;
  background: rgba(203, 226, 219, 0.46);
}

.guide-refuge-mark__logo {
  width: 16px;
  height: 16px;
  display: block;
  object-fit: contain;
}

.locked-guide--streak {
  grid-template-columns: minmax(0, 1fr);
}

.guide-streak-mark {
  width: 30px;
  height: 30px;
  flex: 0 0 30px;
  display: grid;
  place-items: center;
  font-size: 15px;
  line-height: 1;
  border: 1px solid rgba(97, 131, 122, 0.22);
  border-radius: 999px;
  background: rgba(203, 226, 219, 0.34);
}

.locked-text {
  margin: 0;

  color: #36495b;
  font-size: 0.92rem;
  font-weight: 650;
  line-height: 1.4;
}

.locked-description {
  margin: 3px 0 0;

  color: #738087;
  font-size: 0.8rem;
  line-height: 1.45;
}

.locked-cta {
  display: flex;
  align-items: center;
  justify-content: center;
}

.guide-refuge-cta {
  min-height: 40px;
  padding: 10px 15px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #61837a;
  border-radius: 999px;
  background: #61837a;
  color: #ffffff;
  font-size: 0.78rem;
  font-weight: 800;
  line-height: 1;
  white-space: nowrap;
  cursor: pointer;
  box-shadow: 0 8px 18px rgba(97, 131, 122, 0.18);
  transition: transform 180ms ease, background 180ms ease, border-color 180ms ease, box-shadow 180ms ease;
}

.guide-refuge-cta:focus-visible {
  outline: 3px solid rgba(118, 165, 167, 0.3);
  outline-offset: 3px;
}

/* Broché */

.paperback-box {
  min-height: 42px;
  margin-top: 14px;

  display: flex;
  align-items: center;
  justify-content: center;

  border-top: 1px solid rgba(97, 131, 122, 0.11);
  padding-top: 14px;
}

.paperback-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;

  color: #617079;
  font-size: 0.84rem;
  font-weight: 550;
  line-height: 1.4;
  text-align: center;
  text-decoration: none;

  transition:
    color 200ms ease,
    transform 200ms ease;
}

/* Hover uniquement sur les appareils compatibles */

@media (hover: hover) and (pointer: fine) {
  .guide-card:hover {
    transform: translateY(-3px);
    border-color: rgba(97, 131, 122, 0.29);

    box-shadow:
      0 21px 46px rgba(54, 73, 91, 0.1),
      inset 0 1px 0 rgba(255, 255, 255, 0.85);
  }

  .download-btn:hover {
    background: #56776e;
    border-color: #56776e;
    transform: translateY(-1px);
    box-shadow: 0 12px 25px rgba(97, 131, 122, 0.21);
  }

  .paperback-link:hover {
    color: #3f625a;
    transform: translateY(-1px);
  }

  .guide-refuge-cta:hover {
  transform: translateY(-1px);
  background: #56776e;
  border-color: #56776e;
  box-shadow: 0 11px 23px rgba(97, 131, 122, 0.24);
}
}

/* Tablette et mobile */

@media (max-width: 767px) {
  .guides-page {
    margin-top: 94px;
    padding: 0 14px 64px;
    gap: 32px;
  }

  .guides-grid {
    max-width: 500px;
    grid-template-columns: 1fr;
    gap: 20px;
  }

  .guide-card {
    padding: 15px;
    border-radius: 21px;
  }

  .guide-cover {
    height: auto;
    max-height: 240px;
    border-radius: 16px;
  }

  .guide-header {
    align-items: flex-start;
  }

  .guide-title h2 {
    white-space: normal;
  }

  .locked-guide {
    grid-template-columns: 1fr;
  }

  .locked-cta {
    width: 100%;
  }

.guide-refuge-cta {
  width: 100%;
  max-width: none;
}

  .download-btn {
    width: 100%;
  }
}

/* Aucun effet de survol tactile */

@media (hover: none), (pointer: coarse) {
  .guide-card,
  .guide-card:hover,
  .download-btn,
  .download-btn:hover,
  .paperback-link,
  .paperback-link:hover {
    transform: none;
  }
}

/* Accessibilité */

@media (prefers-reduced-motion: reduce) {
  .guide-card,
  .download-btn,
  .paperback-link {
    transition: none;
  }
}
        `}</style>
      </div>
    </>
  );
}